// =============================================================================
// ChildFeesSection.jsx
// =============================================================================
// Per-child school-fees form section used inside the request wizard.
//
// Renders: a repeating row of (fee type, amount, optional brochure file) for
//          a single child. Fee types are state-specific (QLD has extra
//          options); helpers/labels live in formConstants.
//
// Props:
//   fees          - array of fee rows ({ feeType, feeAmount, feeDesc, brochure, ... })
//   onChange      - called with the updated fees array
//   gender        - child's gender (passed through to fee rows)
//   yearLevel     - child's year level (used for stationery cost auto-fill)
//   schoolName    - child's school (passed through to fee rows)
//   user          - current user; staff see extra accounting labels
//   standardCosts - admin-configured standard costs catalogue
//   refNo         - request ref for file upload paths
//   hasError      - if true, highlight section border as invalid
//   requestState  - two-letter state code ("QLD" triggers QLD fee options)
//   hasLaptop     - if true, Device Hire is auto-cleared and blocked
//
// SEE ALSO
//   src/components/form/ChildSections.jsx — re-exports this component
//   src/utils/formConstants.js            — FEE_TYPES_QLD / FEE_TYPES_COMMON / stationeryCost
// =============================================================================
import React from "react";
import { isResponderJob } from "../../app/access";
import { uploadFile } from "../../data/storage";
import { alertDialog } from "../../services/dialogService";
import { validateUploadFile } from "../../services/fileTypeGuard";
import { PANEL } from "../../utils/constants";
import {
  DEFAULT_STANDARD_COSTS,
  FEE_TYPES_COMMON,
  FEE_TYPES_QLD,
  feeTypeLabel,
  SHELTER_FEE_HELP_KEYS,
  stationeryCost,
} from "../../utils/formConstants";
import { money } from "../../utils/helpers";
import { logger } from "../../utils/logger";
import { sanitizeMoney } from "../../utils/sanitize";
import { FileField } from "./FormUI.jsx";
import { HelpButton } from "./HelpButton.jsx";

function ChildFeesSection({
  fees,
  onChange,
  gender,
  yearLevel,
  schoolName,
  user,
  standardCosts,
  refNo,
  hasError,
  requestState,
  hasLaptop,
}) {
  const isQLD = (requestState || user?.state) === "QLD";
  const feeOptions = isQLD ? FEE_TYPES_QLD : FEE_TYPES_COMMON;
  const costs = standardCosts || DEFAULT_STANDARD_COSTS;

  const tagBase = {
    display: "inline-block",
    fontSize: 10,
    fontWeight: 700,
    borderRadius: 4,
    padding: "2px 5px",
    marginLeft: 6,
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  };

  const isShelterUser = !isResponderJob(user);
  function feeTag(feeType) {
    if (isShelterUser) return null;
    if (feeType === "Non-school Activities")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFB300",
          }),
        },
        "NSA SO"
      );
    if (feeType === "Textbooks")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFB300",
          }),
        },
        "REIMB"
      );
    if (feeType === "Swim Gear")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFB300",
          }),
        },
        "REIMB"
      );
    if (feeType === "Other (specify)")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFB300",
          }),
        },
        "REIMB"
      );
    if (feeType === "Stationery & Textbooks")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#FFF3E0",
            color: "#E65100",
            border: "1px solid #FFB300",
          }),
        },
        "REIMB"
      );
    if (feeType === "Stationery")
      return React.createElement(
        "span",
        {
          style: Object.assign({}, tagBase, {
            background: "#E8F5E9",
            color: "#2E7D32",
            border: "1px solid #A5D6A7",
          }),
        },
        "Pickers"
      );
    return null;
  }

  function feesToLines(feesArr) {
    const lines = {};
    (feesArr || []).forEach(function (f) {
      lines[f.feeType] = {
        amount: f.feeAmount || "",
        desc: f.feeDesc || "",
        brochure: f.brochure || "",
      };
    });
    return lines;
  }

  function linesToFees(lines) {
    const flat = [];
    feeOptions.forEach(function (ft) {
      const line = lines[ft];
      if (!line) return;
      const hasAmount = line.amount && parseFloat(line.amount) > 0;
      const hasDesc = line.desc && line.desc.trim().length > 0;
      if (hasAmount || hasDesc) {
        flat.push({
          feeType: ft,
          feeAmount: line.amount || "",
          feeDesc: line.desc || "",
          brochure: line.brochure || "",
          isReimbursement:
            ft === "Textbooks" ||
            ft === "Stationery" ||
            ft === "Stationery & Textbooks" ||
            ft === "Swim Gear" ||
            ft === "Other (specify)",
          isNonSchool: ft === "Non-school Activities",
          gender: gender,
          yearLevel: yearLevel,
          schoolName: schoolName,
        });
      }
    });
    return flat;
  }

  const linesState = React.useState(function () {
    return feesToLines(fees);
  });
  const lines = linesState[0];
  const setLines = linesState[1];

  function upLine(feeType, fields) {
    const newLines = Object.assign({}, lines);
    newLines[feeType] = Object.assign({}, newLines[feeType] || { amount: "", desc: "" }, fields);
    setLines(newLines);
    onChange(linesToFees(newLines));
  }

  // Validate (PDF/JPG/PNG — extension AND magic bytes must agree) then upload one
  // required brochure/letter/agreement. Shared by every fee-attachment field so the
  // gate + the verified content type are applied identically.
  async function pickBrochure(feeType, path, f) {
    if (!f) return;
    const valid = await validateUploadFile(f);
    if (!valid.ok) {
      await alertDialog({ message: valid.error });
      return;
    }
    upLine(feeType, { _brochureUploading: true });
    const res = await uploadFile(path, f, { upsert: true, contentType: valid.contentType });
    if (res.error) {
      logger.warn("Upload failed:", res.error.message);
      upLine(feeType, { _brochureUploading: false });
      return;
    }
    upLine(feeType, { brochure: path, _brochureUploading: false });
  }

  // If a device has been requested for this child, Device Hire is mutually
  // exclusive — auto-clear any value the shelter previously entered.
  React.useEffect(
    function () {
      if (!hasLaptop) return;
      const dh = lines["Device Hire"];
      if (dh && (dh.amount || dh.desc?.trim())) {
        upLine("Device Hire", { amount: "", desc: "" });
      }
    },
    [hasLaptop]
  );

  // ChildTab re-prices a ticked stationery pack when the year level changes, which
  // updates `fees` from outside. Copy that new amount into the local lines so the
  // pack box shows it and a later edit to another fee does not write the old price back.
  const propStaFee = (fees || []).find(function (f) {
    return f.feeType === "Stationery";
  });
  const propStaAmount = propStaFee ? propStaFee.feeAmount || "" : "";
  React.useEffect(
    function () {
      const cur = lines.Stationery;
      if (!propStaFee || !cur || (cur.amount || "") === propStaAmount) return;
      setLines(
        Object.assign({}, lines, {
          Stationery: Object.assign({}, cur, { amount: propStaAmount }),
        })
      );
    },
    [propStaAmount]
  );

  const total = (fees || []).reduce(function (s, f) {
    return s + (parseFloat(f.feeAmount) || 0);
  }, 0);
  const inputBase = {
    border: "1px solid #CFD8DC",
    borderRadius: 5,
    padding: "5px 7px",
    fontSize: 13,
    fontFamily: "inherit",
    background: "#fff",
    color: "#263238",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        border: `1px solid ${hasError ? "#C62828" : "#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: hasError ? "#FFEBEE" : PANEL.bg,
          borderBottom: `1px solid ${hasError ? "#EF9A9A" : PANEL.border}`,
          padding: "10px 16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{ fontWeight: 700, fontSize: 14, color: hasError ? "#C62828" : PANEL.heading }}
        >
          School Costs &amp; Stationery
          {isShelterUser && <HelpButton helpKey="fees_overview" user={user} />}
        </span>
        {total > 0 && (
          <span style={{ fontWeight: 700, color: PANEL.heading, fontSize: 14 }}>
            {money(total)}
          </span>
        )}
      </div>
      <div style={{ padding: "12px 16px" }}>
        {!isShelterUser && (
          <div style={{ fontSize: 12, color: "#607D79", marginBottom: 10 }}>
            {isQLD
              ? "Stationery packs are fulfilled by the Zephyr warehouse (Pickers). Textbooks are DFV Service reimbursement."
              : "Stationery packs are fulfilled by the Zephyr warehouse (Pickers). Stationery & Textbooks are DFV Service reimbursement. Non-school Activities generate a separate NSA SO."}
          </div>
        )}

        {feeOptions.map(function (ft) {
          const line = lines[ft] || { amount: "", desc: "" };
          const hasVal = line.amount && parseFloat(line.amount) > 0;
          const isSta = ft === "Stationery";

          const deviceHireBlocked = ft === "Device Hire" && hasLaptop;

          return (
            <div
              key={ft}
              style={{
                marginBottom: 10,
                background: deviceHireBlocked ? "#FAFAFA" : hasVal ? "#F9FBF9" : "transparent",
                border: `1px solid ${hasVal ? "#00897B" : "#CFE0DE"}`,
                borderRadius: 8,
                padding: "10px 12px",
                opacity: deviceHireBlocked ? 0.6 : 1,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: hasVal ? 6 : 0,
                  flexWrap: "wrap",
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 600, color: "#1A2E2B" }}>
                  {feeTypeLabel(ft)}
                  {feeTag(ft)}
                  {isShelterUser && SHELTER_FEE_HELP_KEYS[ft] && (
                    <HelpButton helpKey={SHELTER_FEE_HELP_KEYS[ft]} user={user} />
                  )}
                  {deviceHireBlocked && (
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#C62828",
                        fontStyle: "italic",
                      }}
                    >
                      Not available — a device has been requested below. Choose either a device or
                      device hire, not both.
                    </span>
                  )}
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    pointerEvents: deviceHireBlocked ? "none" : "auto",
                  }}
                >
                  {isSta ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: 13,
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={hasVal}
                          onChange={function () {
                            upLine(
                              ft,
                              hasVal
                                ? { amount: "", desc: "" }
                                : {
                                    amount: String(stationeryCost(costs, yearLevel)),
                                    desc: "Stationery pack required",
                                  }
                            );
                          }}
                          style={{ accentColor: "#00897B" }}
                        />
                        {"Request stationery pack"}
                      </label>
                      {hasVal && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div
                            style={Object.assign({}, inputBase, {
                              width: 90,
                              textAlign: "right",
                              background: "#F5F7F7",
                              color: "#00695C",
                              fontWeight: 600,
                            })}
                          >
                            {money(line.amount)}
                          </div>
                          <span style={{ fontSize: 11, color: "#607D79", fontStyle: "italic" }}>
                            Approx cost
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 3,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span
                          style={{
                            padding: "5px 6px",
                            background: "#ECEFF1",
                            border:
                              "1px solid " +
                              (line.desc?.trim() && !parseFloat(line.amount)
                                ? "#C62828"
                                : "#CFD8DC"),
                            borderRight: "none",
                            borderRadius: "5px 0 0 5px",
                            fontSize: 13,
                            color: "#607D79",
                          }}
                        >
                          $
                        </span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={line.amount || ""}
                          onChange={function (e) {
                            upLine(ft, { amount: sanitizeMoney(e.target.value) });
                          }}
                          placeholder="0.00"
                          style={Object.assign({}, inputBase, {
                            width: 82,
                            textAlign: "right",
                            borderRadius: "0 5px 5px 0",
                            border:
                              line.desc?.trim() && !parseFloat(line.amount)
                                ? "1px solid #C62828"
                                : undefined,
                            background:
                              line.desc?.trim() && !parseFloat(line.amount) ? "#FFEBEE" : undefined,
                          })}
                        />
                      </div>
                      {line.desc?.trim() && !parseFloat(line.amount) && (
                        <div style={{ fontSize: 11, color: "#C62828", fontWeight: 600 }}>
                          Amount required
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {hasVal && ft === "Camp Fees" && (
                <div
                  style={{
                    background: "#E8F5E9",
                    border: "1px solid #A5D6A7",
                    borderRadius: 6,
                    padding: "10px",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#1B5E20", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700 }}>Camp Fees — brochure required: </span>
                    Please upload a brochure showing camp dates and when deposit and balance
                    payments are due.
                  </div>
                  <FileField
                    label={"Camp Fees Brochure (required) *"}
                    file={lines[ft]?.brochure || null}
                    uploading={!!lines[ft]?._brochureUploading}
                    onChange={function (f) {
                      if (!f) return;
                      pickBrochure(
                        ft,
                        `req-${refNo || "draft"}/camp-brochure-${Date.now()}-${f.name}`,
                        f
                      );
                    }}
                  />
                </div>
              )}
              {hasVal && ft === "Resource Fees" && (
                <div
                  style={{
                    background: "#E8F5E9",
                    border: "1px solid #A5D6A7",
                    borderRadius: 6,
                    padding: "10px",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#1B5E20", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700 }}>Resource Fees — letter required: </span>
                    Please upload a letter from the school declining to waive the fees.
                  </div>
                  <FileField
                    label={"School Letter (required) *"}
                    file={lines[ft]?.brochure || null}
                    uploading={!!lines[ft]?._brochureUploading}
                    onChange={function (f) {
                      if (!f) return;
                      pickBrochure(
                        ft,
                        `req-${refNo || "draft"}/resource-fees-letter-${Date.now()}-${f.name}`,
                        f
                      );
                    }}
                  />
                </div>
              )}
              {hasVal && ft === "Device Hire" && (
                <div
                  style={{
                    background: "#E8F5E9",
                    border: "1px solid #A5D6A7",
                    borderRadius: 6,
                    padding: "10px",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#1B5E20", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700 }}>Device Hire — agreement required: </span>
                    Please upload the school's device hire agreement.
                  </div>
                  <FileField
                    label={"Device Hire Agreement (required) *"}
                    file={lines[ft]?.brochure || null}
                    uploading={!!lines[ft]?._brochureUploading}
                    onChange={function (f) {
                      if (!f) return;
                      pickBrochure(
                        ft,
                        `req-${refNo || "draft"}/device-hire-agreement-${Date.now()}-${f.name}`,
                        f
                      );
                    }}
                  />
                </div>
              )}
              {hasVal && ft === "Textbooks" && (
                <div
                  style={{
                    background: "#E8F5E9",
                    border: "1px solid #A5D6A7",
                    borderRadius: 6,
                    padding: "10px",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ fontSize: 12, color: "#1B5E20", marginBottom: 8 }}>
                    <span style={{ fontWeight: 700 }}>Textbooks — list (optional): </span>
                    If you have it, upload the textbook list.
                  </div>
                  <FileField
                    label={"Textbook List (optional)"}
                    file={lines[ft]?.brochure || null}
                    uploading={!!lines[ft]?._brochureUploading}
                    onChange={function (f) {
                      if (!f) return;
                      pickBrochure(
                        ft,
                        `req-${refNo || "draft"}/textbook-list-${Date.now()}-${f.name}`,
                        f
                      );
                    }}
                  />
                </div>
              )}
              {hasVal && !isSta && (
                <div>
                  <input
                    type="text"
                    value={line.desc || ""}
                    onChange={function (e) {
                      upLine(ft, { desc: e.target.value });
                    }}
                    placeholder={`Description / details for ${ft} (required)`}
                    style={Object.assign({}, inputBase, {
                      width: "100%",
                      border: hasVal && !line.desc?.trim() ? "1px solid #C62828" : undefined,
                      background: hasVal && !line.desc?.trim() ? "#FFEBEE" : undefined,
                    })}
                  />
                  {hasVal && !line.desc?.trim() && (
                    <div style={{ fontSize: 11, color: "#C62828", marginTop: 3, fontWeight: 600 }}>
                      Description is required when an amount is entered
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {total > 0 && (
          <div
            style={{
              textAlign: "right",
              fontWeight: 700,
              color: "#00695C",
              fontSize: 14,
              marginTop: 8,
              paddingTop: 8,
              borderTop: `1px solid ${"#CFE0DE"}`,
            }}
          >
            {`Fees total: ${money(total)}`}
          </div>
        )}
      </div>
    </div>
  );
}

export { ChildFeesSection };
